import BatteryIcon from "../../common/BatteryIcon";
import { Row } from "../../common/Row";
import { StatusDot } from "../../common/StatusDot";
import { useT } from "../../../i18n";
import { deviceOnline, findFog, sortFogFirst } from "../../../utils/energyDevices";
import "./Information.css";

// 정보 탭의 에너지 장치 카드. 구성(mode)에 따라 모양이 갈린다.
//   POLE      : "스마트 폴" 카드에 줄 1개 (지금까지의 화면).
//   EDGE_ONLY : "솔라 큐브 N대" 카드에 큐브마다 줄 1개. 역할 배지는 안 붙인다 — 연결할 상대도
//               없는데 fog/edge 를 보여주면 헷갈린다.
//   FOG       : fog 줄을 맨 위에, edge 줄은 들여써서 아래에. 제목 옆에 "N대 중 M대 정상".
//   NONE      : 연결된 장치 없음 안내.
// 상태(동작 · 배터리 · 연결)만 보여준다. 발전 · 소비는 에너지 탭 몫이다.

const normalizeBatteryPercent = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.max(0, Math.min(100, Math.round(num)));
};

const CUBE_ICON = (
  <>
    <path d="M12 2.6l8 4.4v9l-8 4.4-8-4.4v-9z" />
    <path d="M4 7l8 4.4L20 7" />
    <path d="M12 11.4V20.4" />
  </>
);
const POLE_ICON = <path d="M12 3v18M7 8h10M6 21h12" />;

export const deviceRowId = (device) => device.id ?? device.serialNum;

// 장치 1대 줄. 접으면 요약 한 줄, 펼치면 동작 상태 · 배터리 · (fog 구성) 역할 · 시리얼.
function DeviceStatusRow({ device, mode, open, onToggle }) {
  const t = useT();
  const online = deviceOnline(device);
  const offline = online === false;
  // heartbeat 가 아직 없는 장치는 연결 여부도 동작 상태도 모른다. 이상으로 몰지 않고 "확인 전".
  const unknown = online == null && device.operatingStatus == null;
  const battery = offline ? null : normalizeBatteryPercent(device.batteryCharge);
  const isFog = mode === "FOG" && device.role === "FOG";
  const showRole = mode === "FOG" && device.deviceType === "CUBE" && device.role;

  // Offline 시 default 강제: 동작 상태 Abnormal(빨간 dot) / 배터리 '-'.
  const summary = offline
    ? t("information.summary.disconnected")
    : unknown
      ? t("information.summary.unknown")
      : [
          device.operatingStatus
            ? t("information.summary.operatingNormal")
            : t("information.summary.operatingAbnormal"),
          battery == null ? null : t("information.summary.battery", { percent: battery }),
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <>
      <button
        type="button"
        className={`calm-dev${offline ? " off" : ""}${isFog ? " fog" : ""}${open ? " open" : ""}`}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="ic">
          <svg viewBox="0 0 24 24">{device.deviceType === "CUBE" ? CUBE_ICON : POLE_ICON}</svg>
        </span>
        <span className="info">
          <span className="n">
            {device.name}
            {showRole && (
              <span className={`sb-role-badge ${device.role.toLowerCase()}`}>
                {t(`information.badge.${device.role}`)}
              </span>
            )}
            {offline && <span className="sb-offline-badge">{t("information.pole.offline")}</span>}
          </span>
          <span className="s">{summary}</span>
        </span>
        <span className="arw" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="calm-dev-body">
          <Row label={t("information.pole.operatingStatus")}>
            {offline ? (
              <>
                {t("information.pole.abnormal")}
                <StatusDot text={false} />
              </>
            ) : unknown ? (
              "-"
            ) : (
              <>
                {device.operatingStatus
                  ? t("information.pole.normal")
                  : t("information.pole.abnormal")}
                <StatusDot text={Boolean(device.operatingStatus)} />
              </>
            )}
          </Row>
          <Row label={t("information.pole.battery")}>
            {battery == null ? (
              "-"
            ) : (
              <span className="percent">
                {battery}%
                <BatteryIcon level={battery} />
              </span>
            )}
          </Row>
          {showRole && (
            <Row label={t("information.device.role")}>{t(`information.role.${device.role}`)}</Row>
          )}
          {device.serialNum && (
            <Row label={t("information.device.serial")}>{device.serialNum}</Row>
          )}
        </div>
      )}
    </>
  );
}

export default function EnergyDeviceCard({ energyDevices, openIds, onToggle, toolbar = null }) {
  const t = useT();
  const mode = energyDevices?.mode ?? "NONE";
  const devices = sortFogFirst(energyDevices?.devices ?? []);
  const isOpen = (device) => openIds.has(deviceRowId(device));
  const row = (device) => (
    <DeviceStatusRow
      key={deviceRowId(device)}
      device={device}
      mode={mode}
      open={isOpen(device)}
      onToggle={() => onToggle(deviceRowId(device))}
    />
  );

  if (mode === "NONE" || devices.length === 0) {
    return (
      <section className="sb-card">
        <h3 className="sb-card-title">{t("information.device.none.title")}</h3>
        {toolbar}
        <div className="sb-note">{t("information.device.none.body")}</div>
      </section>
    );
  }

  if (mode === "POLE") {
    const pole = devices[0];
    const offline = deviceOnline(pole) === false;
    return (
      <section className={`sb-card${offline ? " sb-card--offline" : ""}`}>
        <h3 className="sb-card-title">{t("information.pole.smartPole")}</h3>
        {toolbar}
        {row(pole)}
      </section>
    );
  }

  const fog = mode === "FOG" ? findFog(devices) : null;
  const fogDown = fog != null && deviceOnline(fog) === false;
  // "N대 중 M대 정상" 은 연결 여부를 아는 장치가 하나라도 있을 때만. 전부 heartbeat 전이면 숫자가 의미 없다.
  const showCount = devices.some(
    (device) => deviceOnline(device) != null || device.operatingStatus != null
  );
  const okCount = devices.filter(
    (device) => deviceOnline(device) !== false && Boolean(device.operatingStatus)
  ).length;
  const edges = fog ? devices.filter((device) => device !== fog) : devices;

  return (
    <section className={`sb-card${fogDown ? " sb-card--offline" : ""}`}>
      <h3 className="sb-card-title">
        {t("information.cube.titleCount", { count: devices.length })}
        {/* fog 구성은 줄마다 FOG/EDGE 배지가 붙어 제목 배지가 중복이다. 엣지만 구성은 줄에 배지가 없어 여기서 알린다. */}
        {mode !== "FOG" && (
          <span className="sb-mode-badge none">{t("information.cube.edgeOnly")}</span>
        )}
        {showCount && (
          <span className="sb-card-count">
            {t("information.cube.okCount", { total: devices.length, ok: okCount })}
          </span>
        )}
      </h3>
      {toolbar}
      {/* fog 가 끊기면 카드 테두리만 빨갛게 하고 따로 경고문은 두지 않는다 — 바로 아래 fog 줄에
          OFFLINE · "연결 끊김" 이 이미 보여서 같은 말을 두 번 하게 된다. */}
      {fog ? (
        <>
          {row(fog)}
          <div className="calm-dev-indent">{edges.map(row)}</div>
        </>
      ) : (
        edges.map(row)
      )}
    </section>
  );
}
